import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { KmlService, type UploadedKml } from '../../services/kml/kmlService';
import { useTranslation } from 'react-i18next';

// ===============================
// INTERFACES
// ===============================

interface KmlDropzoneProps {
  onKmlUploaded: (kml: UploadedKml) => void;
  maxFiles?: number;
  disabled?: boolean;
  currentFiles?: UploadedKml[];
}

// ===============================
// COMPONENTE DROPZONE KML
// ===============================

export const KmlDropzone: React.FC<KmlDropzoneProps> = ({ 
  onKmlUploaded, 
  maxFiles = 5,
  disabled = false,
  currentFiles = []
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: string}>({});

  // Configuración de react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.google-earth.kml+xml': ['.kml'],
      'application/vnd.google-earth.kmz': ['.kmz'],
      'application/octet-stream': ['.kml', '.kmz'], // Algunos navegadores envían este MIME type
    },
    maxFiles: maxFiles - currentFiles.length,
    maxSize: 50 * 1024 * 1024, // 50MB máximo
    disabled: disabled || uploading,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach(rejection => {
        const file = rejection.file;
        const errors = rejection.errors.map(e => {
          if (e.code === 'file-invalid-type') {
            return `Tipo de archivo no válido. Solo se permiten archivos .kml y .kmz`;
          }
          if (e.code === 'file-too-large') {
            return `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo: 50MB`;
          }
          if (e.code === 'too-many-files') {
            return `Demasiados archivos. Máximo: ${maxFiles} archivos por faena`;
          }
          return e.message;
        });
        toast.error(`${file.name}: ${errors.join(', ')}`);
      });
    },
    onDrop: async (acceptedFiles) => {
      // Los archivos rechazados ya se manejan en onDropRejected
      
      // Procesar archivos aceptados
      if (acceptedFiles.length > 0) {
        await handleFilesUpload(acceptedFiles);
      }
    }
  });

  /**
   * Procesar archivos seleccionados
   */
  const handleFilesUpload = async (files: File[]) => {
    setUploading(true);

    try {
      for (const file of files) {
        // Actualizar progreso
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 'Validando archivo...'
        }));

        // 1. Validación previa
        try {
          const validation = await KmlService.validate(file);
          
          if (!validation.es_valido) {
            toast.error(`${file.name}: ${validation.errores.join(', ')}`);
            continue;
          }

          // Mostrar warnings si los hay
          if (validation.warnings && validation.warnings.length > 0) {
            validation.warnings.forEach(warning => {
              toast(`${file.name}: ${warning}`, { 
                duration: 4000,
                icon: '⚠️'
              });
            });
          }

        } catch (error) {
          console.error('Error validating file:', error);
          toast.error(`Error validando ${file.name}: ${error instanceof Error ? error.message : 'Error desconocido'}`);
          continue;
        }

        // 2. Calcular área real usando API
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 'Calculando área desde polígonos...'
        }));

        try {
          const uploadedKml = await KmlService.uploadTemporal(file);
          
          // 3. Notificar éxito con área real
          onKmlUploaded(uploadedKml);
          
          toast.success(`✅ ${file.name}: ${uploadedKml.area_hectareas} ha calculadas`);
          
        } catch (error) {
          console.error('Error uploading file:', error);
          toast.error(`❌ Error procesando ${file.name}: ${error instanceof Error ? error.message : 'Error desconocido'}`);
        }

        // Limpiar progreso individual
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }

    } catch (error) {
      console.error('Error in file upload process:', error);
      toast.error('Error en el proceso de carga de archivos');
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  // Verificar si se alcanzó el límite de archivos
  const hasReachedLimit = currentFiles.length >= maxFiles;
  const remainingSlots = maxFiles - currentFiles.length;

  return (
    <div className="w-full">
      {/* Dropzone */}
      <div 
        {...getRootProps()} 
        className={`
          border-2 border-dashed rounded-lg p-6 transition-all duration-200 cursor-pointer
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : disabled || hasReachedLimit
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
        `}
      >
        <input {...getInputProps()} />
        
        <div className="text-center">
          {/* Estados del dropzone */}
          {uploading ? (
            <div className="space-y-2">
              <div className="text-blue-600">
                <svg className="w-8 h-8 mx-auto animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <p className="text-blue-600 font-medium">{t('kml.processing')}</p>
              
              {/* Mostrar progreso individual */}
              {Object.entries(uploadProgress).map(([filename, status]) => (
                <p key={filename} className="text-xs text-blue-500">
                  {filename}: {status}
                </p>
              ))}
            </div>
          ) : hasReachedLimit ? (
            <div className="space-y-2">
              <div className="text-gray-400">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-500 font-medium">{t('kml.max_files_reached')}</p>
              <p className="text-xs text-gray-400">{t('kml.max_size')} — {maxFiles} KML</p>
            </div>
          ) : isDragActive ? (
            <div className="space-y-2">
              <div className="text-blue-500">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <p className="text-blue-500 font-medium">{t('kml.drag_drop')}</p>
            </div>
          ) : disabled ? (
            <div className="space-y-2">
              <div className="text-gray-400">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <p className="text-gray-400 font-medium">{t('common.disabled')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-gray-400">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">
                📁 {t('kml.drag_drop')} {t('kml.or_click')}
              </p>
              <div className="text-xs text-gray-500 space-y-1">
                <p>{t('kml.accepted_formats')} • {t('kml.max_size')}</p>
                <p>{t('common.showing')} {remainingSlots} {t('common.of')} {maxFiles}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KmlDropzone;